import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, Phone, Mail, MapPin, Calendar, MessageSquare, Sparkles, 
  Flame, ThermometerSun, Snowflake, Clock, ChevronRight, Plus,
  Building2, IndianRupee, Home, User, RefreshCw, Zap, Send, TrendingUp,
  Target, Route, FileText, Heart, Activity, Gauge, Link2, Unlink, UsersRound, Pencil, Briefcase, ArrowRightLeft, Lock, Trash2, AlertTriangle, Check, Mic
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../components/ui/dropdown-menu";
import Layout from "../components/Layout";
import DynamicFormRenderer from "../components/DynamicFormRenderer";
import AIFeedback from "../components/AIFeedback";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { format } from "date-fns";
import { LOST_REASONS, SOURCES, CONFIGURATIONS, LOCALITIES } from "../constants/dropdownOptions";
import { stripMarkdown, stripMarkdownObj } from "../utils/stripMarkdown";
import { CopyButton } from "../components/CopyButton";
import { usePermissions } from "../hooks/usePermissions";
import EMICalculator from "../components/tools/EMICalculator";
import ProjectComparator from "../components/tools/ProjectComparator";
import MhMSalesCoaching from "../components/tools/mhm/MhMSalesCoaching";
import MhMFlow from "../components/tools/mhm/MhMFlow";
import LeadMhMSessionsPanel from "../components/tools/mhm/LeadMhMSessionsPanel";
import PreCallCopilot from "../components/PreCallCopilot";
import MeetingDebrief from "../components/MeetingDebrief";
import VoiceLogDialog from "../components/VoiceLogDialog";
import LiveSiteVisitDialog from "../components/LiveSiteVisitDialog";
import VisitPrepDialog from "../components/VisitPrepDialog";
import NegotiationForm from "../components/NegotiationForm";
import NurtureJourneyCard from "../components/NurtureJourneyCard";

const SCORE_COLORS = {
  hot: "bg-red-100 text-red-700",
  warm: "bg-amber-100 text-amber-700",
  nurture: "bg-blue-100 text-blue-700",
  dormant: "bg-slate-100 text-slate-500",
  new: "bg-emerald-100 text-emerald-700"
};

const LeadDetailPage = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canEdit, canViewAllLeads } = usePermissions();
  const { user: currentUser } = useAuth();
  const activeTab = searchParams.get("tab") || "interactions";
  const setActiveTab = (val) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (val && val !== "interactions") params.set("tab", val);
      else params.delete("tab");
      return params;
    }, { replace: true });
  };
  // Smart back: use browser history when available (preserves filters, scroll,
  // tab state on the previous page); fall back to /leads on direct landings.
  const smartBack = (fallback = "/leads") => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(fallback);
    }
  };
  const [lead, setLead] = useState(null);
  const [interactions, setInteractions] = useState([]);
  const [interactionsTotal, setInteractionsTotal] = useState(0);
  const [interactionsFilter, setInteractionsFilter] = useState("all");
  const [interactionsTypes, setInteractionsTypes] = useState([]);
  const [interactionsLoadingMore, setInteractionsLoadingMore] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [visits, setVisits] = useState([]);
  const [whatsappMessages, setWhatsappMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [stagePrediction, setStagePrediction] = useState(null);
  const [predictingStage, setPredictingStage] = useState(false);
  const [projectFit, setProjectFit] = useState(null);
  const [gettingProjectFit, setGettingProjectFit] = useState(false);
  const [runningAwareness, setRunningAwareness] = useState(false);
  const [showInteractionDialog, setShowInteractionDialog] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [completeDialog, setCompleteDialog] = useState({ open: false, task: null });
  const [completeOutcome, setCompleteOutcome] = useState("");
  const [completeNotes, setCompleteNotes] = useState("");
  const [showVisitDialog, setShowVisitDialog] = useState(false);
  const [showRevisitDialog, setShowRevisitDialog] = useState(false);
  const [showUnscheduledRevisitDialog, setShowUnscheduledRevisitDialog] = useState(false);
  const [unscheduledRevisitForm, setUnscheduledRevisitForm] = useState({});
  const [backwardStageDialog, setBackwardStageDialog] = useState({ open: false, targetStage: "" });
  const [backwardReason, setBackwardReason] = useState("");
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [newInteraction, setNewInteraction] = useState({});
  const [newTask, setNewTask] = useState({ title: "", description: "", task_type: "call", due_date: new Date(), priority: "medium" });
  const [newVisit, setNewVisit] = useState({});
  const [selectedMsgTemplate, setSelectedMsgTemplate] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [msgChannel, setMsgChannel] = useState("whatsapp"); // whatsapp | sms | email
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [channelTemplates, setChannelTemplates] = useState([]);
  const [smsMessages, setSmsMessages] = useState([]);
  const [emailMessages, setEmailMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [enrichment, setEnrichment] = useState(null);
  const [familyGroups, setFamilyGroups] = useState([]);
  const [calculatingScores, setCalculatingScores] = useState(false);
  const [lostDialogOpen, setLostDialogOpen] = useState(false);
  const [lostReason, setLostReason] = useState("");
  const [lostSubReason, setLostSubReason] = useState("");
  const [lostNotes, setLostNotes] = useState("");
  const [linkedLeads, setLinkedLeads] = useState([]);
  const [editLeadOpen, setEditLeadOpen] = useState(false);
  const [editLeadForm, setEditLeadForm] = useState(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferProject, setTransferProject] = useState("");
  const [transferNotes, setTransferNotes] = useState("");
  const [transferring, setTransferring] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);
  const [reEngageDialog, setReEngageDialog] = useState({ open: false, notes: "" });
  const [leadEois, setLeadEois] = useState([]);
  const [repeatBuyerDialog, setRepeatBuyerDialog] = useState(false);
  const [repeatBuyerNotes, setRepeatBuyerNotes] = useState("");
  const [creatingRepeatBuyer, setCreatingRepeatBuyer] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignDialogOpen, setCampaignDialogOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [taggingCampaign, setTaggingCampaign] = useState(false);
  const [prepMeOpen, setPrepMeOpen] = useState(false);
  const [debriefOpen, setDebriefOpen] = useState(false);
  const [voiceLogOpen, setVoiceLogOpen] = useState(false);
  const [voiceLogDefaultType, setVoiceLogDefaultType] = useState(null);
  const [voiceLogTaskId, setVoiceLogTaskId] = useState(null);
  const [voiceLogSiteVisitId, setVoiceLogSiteVisitId] = useState(null);
  const [liveVisitOpen, setLiveVisitOpen] = useState(false);
  const [debriefVisitId, setDebriefVisitId] = useState(null);
  const [visitPrepOpen, setVisitPrepOpen] = useState(false);
  const [negotiationFormOpen, setNegotiationFormOpen] = useState(false);

  const ADMIN_ROLES = ['admin', 'ceo'];
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);

  // Auto-open negotiation form if redirected from queue claim
  useEffect(() => {
    if (searchParams.get("open_negotiation") === "true" && lead) {
      setNegotiationFormOpen(true);
      setSearchParams({}, { replace: true }); // Clean URL
    }
  }, [lead, searchParams]);

  useEffect(() => {
    fetchLeadData();
    fetchUsers();
    fetchProjects();
    fetchCampaigns();
    fetchWhatsAppData();
    fetchEnrichment();
    fetchLinkedLeads();
    fetchReferralInfo();
    fetchLeadEois();
  }, [leadId]);

  const fetchLinkedLeads = async () => {
    try {
      const res = await axios.get(`${API}/leads/${leadId}/linked`);
      setLinkedLeads(res.data.linked_leads || []);
    } catch { setLinkedLeads([]); }
  };

  const fetchReferralInfo = async () => {
    try {
      const res = await axios.get(`${API}/referrals/lead/${leadId}`);
      setReferralInfo(res.data);
    } catch { setReferralInfo(null); }
  };

  const fetchLeadEois = async () => {
    try {
      const res = await axios.get(`${API}/eoi?lead_id=${leadId}`);
      setLeadEois(res.data || []);
    } catch { setLeadEois([]); }
  };

  const fetchLeadData = async () => {
    try {
      const response = await axios.get(`${API}/leads/${leadId}`);
      setLead(response.data.lead);
      setInteractions(response.data.interactions);
      setInteractionsTotal(response.data.interactions_total ?? response.data.interactions.length);
      setTasks(response.data.tasks);
      setVisits(response.data.visits);
      // Fetch type counts in background (cheap aggregation) for the filter chips
      axios.get(`${API}/interactions/${leadId}?include_counts=true&limit=1`)
        .then(r => setInteractionsTypes(r.data.types || []))
        .catch(() => setInteractionsTypes([]));
    } catch (error) {
      console.error("Fetch lead error:", error);
      toast.error("Failed to fetch lead details");
    } finally {
      setLoading(false);
    }
  };

  const loadMoreInteractions = async () => {
    if (interactionsLoadingMore) return;
    setInteractionsLoadingMore(true);
    try {
      const params = new URLSearchParams({
        skip: String(interactions.length),
        limit: "30",
      });
      if (interactionsFilter && interactionsFilter !== "all") params.set("type", interactionsFilter);
      const res = await axios.get(`${API}/interactions/${leadId}?${params}`);
      setInteractions(prev => [...prev, ...(res.data.items || [])]);
      setInteractionsTotal(res.data.total ?? interactionsTotal);
    } catch (e) {
      toast.error("Couldn't load more interactions");
    } finally {
      setInteractionsLoadingMore(false);
    }
  };

  const applyInteractionFilter = async (typeKey) => {
    setInteractionsFilter(typeKey);
    setInteractionsLoadingMore(true);
    try {
      const params = new URLSearchParams({ skip: "0", limit: "30" });
      if (typeKey && typeKey !== "all") params.set("type", typeKey);
      const res = await axios.get(`${API}/interactions/${leadId}?${params}`);
      setInteractions(res.data.items || []);
      setInteractionsTotal(res.data.total ?? 0);
    } catch (e) {
      toast.error("Couldn't apply filter");
    } finally {
      setInteractionsLoadingMore(false);
    }
  };

  const fetchWhatsAppData = async () => {
    try {
      const [messagesRes, smsRes, emailRes] = await Promise.all([
        axios.get(`${API}/whatsapp/messages/${leadId}`),
        axios.get(`${API}/messages/history/${leadId}?channel=sms`),
        axios.get(`${API}/messages/history/${leadId}?channel=email`),
      ]);
      setWhatsappMessages(messagesRes.data);
      setSmsMessages(smsRes.data);
      setEmailMessages(emailRes.data);
    } catch (error) {
      console.error("Fetch messaging data error:", error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/auth/users`);
      setUsers(response.data);
    } catch (error) {
      console.error("Fetch users error:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error("Fetch projects error:", error);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data || []);
    } catch (error) {
      console.error("Fetch campaigns error:", error);
    }
  };

  const fetchEnrichment = async () => {
    try {
      const [profileRes, groupsRes] = await Promise.all([
        axios.get(`${API}/enrichment/profile/${leadId}`).catch(() => null),
        axios.get(`${API}/family-groups/by-lead/${leadId}`).catch(() => ({ data: [] })),
      ]);
      if (profileRes) setEnrichment(profileRes.data);
      setFamilyGroups(groupsRes?.data || []);
    } catch (error) {
      console.error("Enrichment fetch error:", error);
    }
  };

  const handleRecalculateScores = async () => {
    setCalculatingScores(true);
    try {
      await axios.post(`${API}/enrichment/scores/${leadId}`);
      toast.success("Scores recalculated");
      fetchEnrichment();
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to recalculate scores");
    } finally {
      setCalculatingScores(false);
    }
  };

  const handleScoreLead = async () => {
    setScoring(true);
    try {
      const response = await axios.post(`${API}/ai/score/${leadId}`);
      toast.success(`Lead scored as ${response.data.score}`);
      fetchLeadData();
    } catch (error) {
      console.error("Score lead error:", error);
      toast.error("Failed to score lead");
    } finally {
      setScoring(false);
    }
  };

  const handleStagePrediction = async () => {
    setPredictingStage(true);
    try {
      const response = await axios.post(`${API}/ai/stage-prediction/${leadId}`);
      setStagePrediction(stripMarkdownObj(response.data));
      toast.success("Stage prediction complete");
    } catch (error) {
      console.error("Stage prediction error:", error);
      toast.error("Failed to get prediction");
    } finally {
      setPredictingStage(false);
    }
  };

  const handleGetProjectFit = async () => {
    setGettingProjectFit(true);
    try {
      const response = await axios.post(`${API}/ai/project-fit/${leadId}`);
      setProjectFit(stripMarkdownObj(response.data));
      toast.success("Project recommendation ready");
    } catch (error) {
      console.error("Project fit error:", error);
      toast.error("Failed to get recommendation");
    } finally {
      setGettingProjectFit(false);
    }
  };

  const handleCreateNurtureJourney = () => {
    // Scroll to the Nurture Journey card; the card itself handles create/load/log/regenerate.
    const el = document.querySelector('[data-testid="nurture-journey-card"]')
      || document.querySelector('[data-testid="nurture-create-btn"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const runAwarenessAnalysis = async () => {
    setRunningAwareness(true);
    try {
      const response = await axios.post(`${API}/awareness/analyze/${leadId}`);
      toast.success(`Awareness: ${response.data.awareness_stage}`);
      fetchLeadData(); // Refresh lead data to show updated awareness
    } catch (error) {
      console.error("Awareness analysis error:", error);
      toast.error("Failed to analyze awareness");
    } finally {
      setRunningAwareness(false);
    }
  };

  const getAwarenessBgColor = (stage) => {
    const colors = {
      "Completely Unaware": "bg-slate-500",
      "Problem Aware": "bg-amber-500",
      "Solution Aware": "bg-blue-500",
      "Product Aware": "bg-purple-500",
      "Most Aware": "bg-green-500"
    };
    return colors[stage] || "bg-slate-500";
  };

  const handleAddInteraction = async () => {
    if (!newInteraction.content) {
      toast.error("Please add content");
      return;
    }
    try {
      const payload = {
        lead_id: leadId,
        type: newInteraction.type || "call",
        content: newInteraction.content
      };
      if (newInteraction.call_duration) payload.call_duration = newInteraction.call_duration;
      if (newInteraction.call_outcome) payload.call_outcome = newInteraction.call_outcome;
      if (newInteraction.follow_up_date) payload.follow_up_date = newInteraction.follow_up_date;
      await axios.post(`${API}/interactions`, payload);
      toast.success("Interaction added");
      setShowInteractionDialog(false);
      const wasCallOnLostLead = (newInteraction.type || "call") === "call" && lead?.stage === "lost";
      setNewInteraction({});
      fetchLeadData();
      if (wasCallOnLostLead) {
        setReEngageDialog({ open: true, notes: "" });
      }
    } catch (error) {
      toast.error("Failed to add interaction");
    }
  };

  const handleReEngage = async () => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { stage: "re_engaged" });
      await axios.post(`${API}/interactions`, {
        lead_id: leadId,
        type: "note",
        content: `Lead re-engaged via phone call.${reEngageDialog.notes ? ` Notes: ${reEngageDialog.notes}` : ""}`
      });
      toast.success("Lead re-engaged successfully");
      setReEngageDialog({ open: false, notes: "" });
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to re-engage lead");
    }
  };

  const openSendDialog = async (channel) => {
    setMsgChannel(channel);
    setSelectedMsgTemplate("");
    setShowSendDialog(true);
    try {
      const res = await axios.get(`${API}/message-templates/approved?channel=${channel}`);
      setChannelTemplates(res.data);
    } catch { setChannelTemplates([]); }
  };

  const getResolvedPreview = (tpl) => {
    if (!tpl || !lead) return "";
    let content = tpl.content || "";
    const varMap = {
      name: lead.name || "there",
      lead_name: lead.name || "there",
      project: lead.project_interest || "",
      phone: lead.phone || "",
      email: lead.email || "",
      agent_name: users.find(u => u.user_id === lead.assigned_to)?.name || "our team",
      company_name: "Our Company",
    };
    Object.entries(varMap).forEach(([k, v]) => { content = content.replace(new RegExp(`\\{${k}\\}`, "g"), v); });
    return content;
  };

  const handleSendMessage = async () => {
    if (!selectedMsgTemplate) {
      toast.error("Please select a template");
      return;
    }
    setSendingMessage(true);
    try {
      if (msgChannel === "whatsapp") {
        const res = await axios.post(`${API}/whatsapp/send`, { lead_id: leadId, template_id: selectedMsgTemplate });
        if (res.data?.success) {
          toast.success("WhatsApp message sent via DoubleTick");
        } else {
          toast.warning(res.data?.note || "WhatsApp message may not have been delivered");
        }
      } else {
        await axios.post(`${API}/messages/send`, { lead_id: leadId, template_id: selectedMsgTemplate, channel: msgChannel });
        toast.success(`${msgChannel === "sms" ? "SMS" : "Email"} message queued`);
      }
      setShowSendDialog(false);
      setSelectedMsgTemplate("");
      fetchWhatsAppData();
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to send ${msgChannel}`);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAddTask = async () => {
    try {
      const autoTitle = (newTask.title && newTask.title.trim())
        ? newTask.title.trim()
        : `${(newTask.task_type || "follow_up").replace(/_/g, " ")} - ${lead?.name || "Lead"}`;
      await axios.post(`${API}/tasks`, {
        lead_id: leadId,
        title: autoTitle,
        description: newTask.description,
        task_type: newTask.task_type,
        due_date: newTask.due_date.toISOString(),
        priority: newTask.priority
      });
      toast.success("Task created");
      setShowTaskDialog(false);
      setNewTask({ title: "", description: "", task_type: "call", due_date: new Date(), priority: "medium" });
      fetchLeadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to create task");
    }
  };

  const handleCompleteTask = async (taskId, outcome, outcomeNotes) => {
    try {
      await axios.put(`${API}/tasks/${taskId}/complete`, {
        outcome: outcome || null,
        outcome_notes: outcomeNotes || null,
      });
      toast.success("Task completed");
      fetchLeadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to complete task");
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      toast.success("Task deleted");
      fetchLeadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to delete task");
    }
  };

  const handleScheduleVisit = async () => {
    if (!newVisit.scheduled_date || !newVisit.project) {
      toast.error("Date and project are required");
      return;
    }
    try {
      const payload = {
        lead_id: leadId,
        scheduled_date: new Date(newVisit.scheduled_date).toISOString(),
        project: newVisit.project,
        pre_visit_notes: newVisit.pre_visit_notes || ""
      };
      if (newVisit.visit_time) payload.visit_time = newVisit.visit_time;
      if (newVisit.num_visitors) payload.num_visitors = newVisit.num_visitors;
      if (newVisit.pickup_required) payload.pickup_required = newVisit.pickup_required;
      if (newVisit.pickup_location) payload.pickup_location = newVisit.pickup_location;
      await axios.post(`${API}/visits`, payload);
      toast.success("Site visit scheduled");
      setShowVisitDialog(false);
      setNewVisit({});
      fetchLeadData();
    } catch (error) {
      toast.error("Failed to schedule visit");
    }
  };

  const handleScheduleRevisit = async () => {
    if (!newVisit.scheduled_date || !newVisit.project) {
      toast.error("Date and project are required");
      return;
    }
    try {
      const payload = {
        lead_id: leadId,
        scheduled_date: new Date(newVisit.scheduled_date).toISOString(),
        visit_type: "revisit",
        project: newVisit.project,
        pre_visit_notes: newVisit.pre_visit_notes || ""
      };
      if (newVisit.visit_time) payload.visit_time = newVisit.visit_time;
      if (newVisit.revisit_reason) payload.revisit_reason = newVisit.revisit_reason;
      await axios.post(`${API}/visits`, payload);
      toast.success("Revisit scheduled — lead stage updated to Revisit");
      setShowRevisitDialog(false);
      setNewVisit({});
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to schedule revisit");
    }
  };

  const handleLogUnscheduledRevisit = async () => {
    const pvd = unscheduledRevisitForm;
    if (!pvd.interest_level || !pvd.buyer_mood) {
      toast.error("Interest Level and Buyer Mood are required");
      return;
    }
    try {
      // Create a completed visit record directly
      await axios.post(`${API}/visits`, {
        lead_id: leadId,
        scheduled_date: new Date().toISOString(),
        visit_type: "revisit",
        project: lead?.project_interest || "",
        status: "completed",
        is_unscheduled: true,
        post_visit_feedback: pvd.notes || "",
        interest_level: pvd.interest_level,
        post_visit_analysis: pvd,
      });
      toast.success("Unscheduled revisit logged with analysis");
      setShowUnscheduledRevisitDialog(false);
      setUnscheduledRevisitForm({});
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to log revisit");
    }
  };

  const handleTransferToProject = async () => {
    if (!transferProject) {
      toast.error("Please select a project");
      return;
    }
    if (transferProject.toLowerCase() === (lead.project_interest || "").toLowerCase()) {
      toast.error("Please select a different project");
      return;
    }

    setTransferring(true);
    try {
      // Create a linked lead for the new project using existing duplicate handling
      const response = await axios.post(`${API}/leads`, {
        name: lead.name,
        phone: lead.phone,
        email: lead.email,
        source: lead.source,
        project_interest: transferProject,
        budget_min: lead.budget_min,
        budget_max: lead.budget_max,
        configuration: lead.configuration,
        locality_preference: lead.locality_preference,
        notes: transferNotes ? `Transferred from ${lead.project_interest || 'previous project'}. Notes: ${transferNotes}` : `Transferred from ${lead.project_interest || 'previous project'}`
      });

      // Log interaction on original lead
      await axios.post(`${API}/interactions`, {
        lead_id: leadId,
        type: "note",
        content: `Lead transferred to project: ${transferProject}. New Lead ID: ${response.data.lead_id}${transferNotes ? `. Reason: ${transferNotes}` : ''}`
      });

      toast.success(`Lead transferred to ${transferProject}`, {
        description: "A linked lead has been created for the new project",
        duration: 5000
      });
      
      setShowTransferDialog(false);
      setTransferProject("");
      setTransferNotes("");
      fetchLinkedLeads();
      fetchLeadData();

      // Ask if user wants to navigate to new lead
      if (window.confirm(`Go to the new lead for ${transferProject}?`)) {
        navigate(`/leads/${response.data.lead_id}`);
      }
    } catch (error) {
      console.error("Transfer error:", error);
      toast.error(error.response?.data?.detail || "Failed to transfer lead");
    } finally {
      setTransferring(false);
    }
  };

  const STAGE_ORDER = ["new", "contacted", "qualified", "site_visit_scheduled", "site_visit", "revisit", "negotiation_scheduled", "negotiation", "booked", "re_engaged", "lost"];

  const handleDeleteLead = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/leads/${leadId}`);
      toast.success("Lead deleted successfully");
      smartBack("/leads");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete lead");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleTagCampaign = async () => {
    if (!selectedCampaign) {
      toast.error("Please select a campaign");
      return;
    }
    setTaggingCampaign(true);
    try {
      await axios.post(`${API}/leads/${leadId}/tag-campaign`, {
        campaign_id: selectedCampaign
      });
      toast.success("Lead tagged to campaign");
      fetchLeadData();
      setCampaignDialogOpen(false);
      setSelectedCampaign("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to tag campaign");
    } finally {
      setTaggingCampaign(false);
    }
  };

  const handleStageChange = async (newStage) => {
    if (newStage === "lost") {
      setLostReason("");
      setLostSubReason("");
      setLostNotes("");
      setLostDialogOpen(true);
      return;
    }

    // Intercept negotiation stage — open negotiation form
    if (newStage === "negotiation") {
      setNegotiationFormOpen(true);
      return;
    }

    // Detect backward stage movement
    const currentIdx = STAGE_ORDER.indexOf(lead.stage);
    const newIdx = STAGE_ORDER.indexOf(newStage);
    if (newIdx < currentIdx && newIdx >= 0) {
      setBackwardStageDialog({ open: true, targetStage: newStage });
      setBackwardReason("");
      return;
    }

    try {
      await axios.put(`${API}/leads/${leadId}`, { stage: newStage });
      toast.success(`Stage updated to ${newStage}`);
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.status === 403 ? "You can only edit leads assigned to you" : "Failed to update stage");
    }
  };

  const handleConfirmBackwardStage = async () => {
    if (!backwardReason.trim()) {
      toast.error("Please provide a reason for moving the lead backward");
      return;
    }
    try {
      await axios.put(`${API}/leads/${leadId}`, {
        stage: backwardStageDialog.targetStage,
        backward_reason: backwardReason.trim(),
      });
      toast.success(`Stage moved back to ${backwardStageDialog.targetStage.replace(/_/g, " ")}`);
      setBackwardStageDialog({ open: false, targetStage: "" });
      setBackwardReason("");
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.status === 403 ? "You can only edit leads assigned to you" : "Failed to update stage");
    }
  };

  const saveEditLead = async () => {
    if (!editLeadForm) return;
    try {
      await axios.put(`${API}/leads/${leadId}`, {
        name: editLeadForm.name, phone: editLeadForm.phone, email: editLeadForm.email,
        source: editLeadForm.source, project_interest: editLeadForm.project_interest,
        budget_min: Number(editLeadForm.budget_min) || 0, budget_max: Number(editLeadForm.budget_max) || 0,
        configuration: editLeadForm.configuration, locality_preference: editLeadForm.locality_preference,
        notes: editLeadForm.notes, next_action: editLeadForm.next_action,
      });
      // If IVR lead was pending completion, mark as complete
      if (lead?.needs_completion) {
        await axios.put(`${API}/ivr/complete/${leadId}`).catch(() => {});
      }
      toast.success("Lead updated");
      setEditLeadOpen(false);
      fetchLeadData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update lead"); }
  };

  const handleConfirmLost = async () => {
    if (!lostReason) {
      toast.error("Please select a reason");
      return;
    }
    try {
      await axios.put(`${API}/leads/${leadId}`, {
        stage: "lost",
        lost_reason: lostReason,
        lost_sub_reason: lostSubReason || null,
        lost_notes: lostNotes || null,
      });
      toast.success("Lead marked as lost");
      setLostDialogOpen(false);
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update stage");
    }
  };

  const handleAssignmentChange = async (userId) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { assigned_to: userId });
      toast.success("Assignment updated");
      fetchLeadData();
    } catch (error) {
      toast.error(error.response?.status === 403 ? "You can only edit leads assigned to you" : "Failed to update assignment");
    }
  };

  const formatINR = (amount) => {
    if (!amount) return "N/A";
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  // Create repeat buyer enquiry for booked customers
  const handleCreateRepeatBuyerEnquiry = async () => {
    setCreatingRepeatBuyer(true);
    try {
      const response = await axios.post(`${API}/leads`, {
        name: lead.name,
        phone: lead.phone,
        email: lead.email,
        source: "repeat_buyer",
        project_interest: lead.project_interest,
        budget_min: lead.budget_min,
        budget_max: lead.budget_max,
        configuration: lead.configuration,
        locality_preference: lead.locality_preference,
        notes: repeatBuyerNotes || `Repeat buyer enquiry - existing customer interested in another unit. Previous booking: ${lead.lead_id}`,
      });
      
      toast.success("Repeat buyer enquiry created!");
      setRepeatBuyerDialog(false);
      setRepeatBuyerNotes("");
      
      // Navigate to the new lead
      if (response.data?.lead_id) {
        navigate(`/leads/${response.data.lead_id}`);
      } else {
        // Refresh current page to show linked lead
        fetchLeadData();
      }
    } catch (error) {
      console.error("Error creating repeat buyer enquiry:", error);
      toast.error(error.response?.data?.detail || "Failed to create enquiry");
    } finally {
      setCreatingRepeatBuyer(false);
    }
  };

  const getScoreBadge = (score) => {
    const styles = {
      hot: "bg-red-100 text-red-700 border-red-200",
      warm: "bg-amber-100 text-amber-700 border-amber-200",
      nurture: "bg-blue-100 text-blue-700 border-blue-200",
      dormant: "bg-slate-100 text-slate-500 border-slate-200",
      new: "bg-emerald-100 text-emerald-700 border-emerald-200"
    };
    return styles[score] || styles.new;
  };

  const getScoreIcon = (score) => {
    const icons = {
      hot: <Flame className="w-4 h-4" />,
      warm: <ThermometerSun className="w-4 h-4" />,
      nurture: <Snowflake className="w-4 h-4" />,
      dormant: <Clock className="w-4 h-4" />
    };
    return icons[score] || null;
  };

  if (loading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-48 bg-slate-200 rounded"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!lead) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Lead not found</p>
          <Button onClick={() => smartBack("/leads")} className="mt-4">Back to Leads</Button>
        </div>
      </Layout>
    );
  }

  // Ownership-based edit guard: user can edit if they have view_all_leads OR are the assignee
  const canEditThisLead = canEdit("edit_leads") && (canViewAllLeads || currentUser?.user_id === lead?.assigned_to);

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 space-y-6" data-testid="lead-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => smartBack("/leads")} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Manrope']">{lead.name}</h1>
              {canEditThisLead && (
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => { setEditLeadForm({...lead}); setEditLeadOpen(true); }} data-testid="edit-lead-btn">
                  <Pencil className="w-4 h-4 text-slate-500" />
                </Button>
              )}
              <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full border capitalize ${getScoreBadge(lead.score)}`}>
                {getScoreIcon(lead.score)}
                {lead.score}
              </span>
            </div>
            <p className="text-slate-500 mt-1">
              Lead ID: {lead.lead_id}
              {lead.created_at && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs text-slate-500" title={new Date(lead.created_at).toLocaleString()} data-testid="lead-created-at">
                  · Created {new Date(lead.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              )}
              {(lead.parent_lead_id || linkedLeads.length > 0) && (
                <span className="ml-2 inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  <Link2 className="w-3 h-3" /> {linkedLeads.length + 1} linked
                </span>
              )}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setPrepMeOpen(true)}
              data-testid="prep-me-btn"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              <Phone className="w-4 h-4 mr-1" />
              Prep Me
            </Button>
            <Button
              onClick={() => setDebriefOpen(true)}
              data-testid="log-meeting-btn"
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              <Activity className="w-4 h-4 mr-1" />
              Log Meeting
            </Button>
            <Button
              onClick={() => { setVoiceLogDefaultType(null); setVoiceLogTaskId(null); setVoiceLogSiteVisitId(null); setVoiceLogOpen(true); }}
              data-testid="voice-log-btn"
              size="sm"
              className="bg-violet-600 hover:bg-violet-700 text-white"
            >
              <Mic className="w-4 h-4 mr-1" />
              Voice Log
            </Button>
            <Button
              onClick={() => setLiveVisitOpen(true)}
              data-testid="live-visit-btn"
              size="sm"
              className="bg-rose-600 hover:bg-rose-700 text-white"
              title="Record clips while you walk through the site"
            >
              <MapPin className="w-4 h-4 mr-1" />
              Live Visit
            </Button>

            {/* Mobile-only "More" dropdown — collapses secondary actions on phone screens */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="md:hidden" data-testid="lead-actions-more-btn">
                  More
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={handleScoreLead} disabled={scoring} data-testid="more-score-btn">
                  <Sparkles className="w-4 h-4 mr-2" /> Score
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleStagePrediction} disabled={predictingStage} data-testid="more-predict-btn">
                  <TrendingUp className="w-4 h-4 mr-2" /> Predict
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleGetProjectFit} disabled={gettingProjectFit} data-testid="more-projectfit-btn">
                  <Target className="w-4 h-4 mr-2" /> Project Fit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={runAwarenessAnalysis} disabled={runningAwareness} data-testid="more-nextaction-btn">
                  <Zap className="w-4 h-4 mr-2" /> Next Action
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleCreateNurtureJourney} data-testid="more-nurture-btn">
                  <Route className="w-4 h-4 mr-2" /> Nurture
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => {
                  try {
                    const res = await axios.get(`${API}/deal-rooms?lead_id=${leadId}`);
                    const activeDeal = (res.data || []).find(d => d.status === "active");
                    if (activeDeal) navigate(`/deal-rooms/${activeDeal.deal_room_id}`);
                    else toast.info("Deal Room is auto-created when lead moves to Negotiation stage");
                  } catch { toast.error("Failed to check Deal Room"); }
                }} data-testid="more-dealroom-btn">
                  <Briefcase className="w-4 h-4 mr-2" /> Deal Room
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowTransferDialog(true)} data-testid="more-transfer-btn">
                  <ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Project
                </DropdownMenuItem>
                {lead.stage === "booked" && (
                  <DropdownMenuItem onClick={() => setRepeatBuyerDialog(true)} data-testid="more-repeat-btn">
                    <span className="mr-2">🏠</span> Add Another Enquiry
                  </DropdownMenuItem>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => setDeleteDialogOpen(true)} className="text-red-600 focus:text-red-700" data-testid="more-delete-btn">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete Lead
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Desktop-only secondary buttons */}
            <div className="hidden md:contents">
            <Button 
              variant="outline" 
              onClick={handleScoreLead}
              disabled={scoring}
              data-testid="score-lead-btn"
              size="sm"
            >
              {scoring ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Sparkles className="w-4 h-4 mr-1" />}
              Score
            </Button>
            <Button 
              variant="outline"
              onClick={handleStagePrediction}
              disabled={predictingStage}
              data-testid="predict-stage-btn"
              size="sm"
            >
              {predictingStage ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <TrendingUp className="w-4 h-4 mr-1" />}
              Predict
            </Button>
            <Button 
              variant="outline"
              onClick={handleGetProjectFit}
              disabled={gettingProjectFit}
              data-testid="project-fit-btn"
              size="sm"
            >
              {gettingProjectFit ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Target className="w-4 h-4 mr-1" />}
              Project Fit
            </Button>
            <Button 
              onClick={runAwarenessAnalysis}
              disabled={runningAwareness}
              data-testid="next-action-btn"
              size="sm"
              className="bg-amber-500 hover:bg-amber-600 text-white"
            >
              {runningAwareness ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <Zap className="w-4 h-4 mr-1" />}
              Next Action
            </Button>
            <Button 
              variant="outline"
              onClick={handleCreateNurtureJourney}
              data-testid="nurture-journey-btn"
              size="sm"
            >
              <Route className="w-4 h-4 mr-1" />
              Nurture
            </Button>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  // Check if deal room exists for this lead
                  const res = await axios.get(`${API}/deal-rooms?lead_id=${leadId}`);
                  const deals = res.data || [];
                  const activeDeal = deals.find(d => d.status === "active");
                  if (activeDeal) {
                    navigate(`/deal-rooms/${activeDeal.deal_room_id}`);
                  } else {
                    toast.info("Deal Room is auto-created when lead moves to Negotiation stage");
                  }
                } catch { toast.error("Failed to check Deal Room"); }
              }}
              data-testid="open-deal-room-btn"
              size="sm"
              className="text-fuchsia-600 border-fuchsia-200 hover:bg-fuchsia-50"
            >
              <Briefcase className="w-4 h-4 mr-1" /> Deal Room
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowTransferDialog(true)}
              data-testid="transfer-project-btn"
              size="sm"
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              <ArrowRightLeft className="w-4 h-4 mr-1" /> Transfer Project
            </Button>
            {/* Add Another Enquiry - for booked customers */}
            {lead.stage === "booked" && (
              <Button
                variant="outline"
                onClick={() => setRepeatBuyerDialog(true)}
                data-testid="add-repeat-buyer-btn"
                size="sm"
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
              >
                <span className="mr-1">🏠</span> Add Another Enquiry
              </Button>
            )}
            {/* Delete Lead - Admin only */}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="delete-lead-btn"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
            )}
            {/* Archive / Reactivate */}
            {lead.archived ? (
              <Button variant="outline" size="sm" className="text-green-600 border-green-200 hover:bg-green-50"
                data-testid="reactivate-lead-btn"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/leads/${lead.lead_id}/reactivate`, { reason: "Manual reactivation from lead detail" });
                    toast.success("Lead reactivated");
                    window.location.reload();
                  } catch { toast.error("Failed to reactivate"); }
                }}>
                Reactivate
              </Button>
            ) : isAdmin && (
              <Button variant="outline" size="sm" className="text-slate-500 border-slate-200 hover:bg-slate-50"
                data-testid="archive-lead-btn"
                onClick={async () => {
                  try {
                    await axios.post(`${API}/leads/${lead.lead_id}/archive`);
                    toast.success("Lead archived");
                    smartBack("/leads");
                  } catch { toast.error("Failed to archive"); }
                }}>
                Archive
              </Button>
            )}
            </div>
          </div>
        </div>

        {/* Read-only banner for non-assignees */}
        {!canEditThisLead && canEdit("edit_leads") && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3" data-testid="read-only-banner">
            <Lock className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800">
              <span className="font-medium">View only</span> — This lead is assigned to{" "}
              <span className="font-semibold">{users.find(u => u.user_id === lead.assigned_to)?.name || "another user"}</span>.
              Only the assignee or admins can edit.
            </p>
          </div>
        )}

        {/* Repeat Buyer Banner - show if this is a repeat buyer */}
        {lead.is_repeat_buyer && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center text-xl">
                🏠
              </div>
              <div>
                <p className="font-semibold text-amber-800">Repeat Buyer</p>
                <p className="text-sm text-amber-700">This customer has already booked a property and is interested in another unit.</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Stage Prediction Banner */}
        {stagePrediction && (
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200" data-testid="ai-stage-prediction-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-indigo-500 rounded-xl flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <p className="font-semibold text-indigo-800">Stage Prediction Analysis</p>
                </div>
                <div className="flex items-center gap-2">
                  <AIFeedback 
                    feature="stage_prediction"
                    leadId={leadId}
                    aiResponse={stagePrediction}
                    compact={true}
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                    onClick={() => setStagePrediction(null)}
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-white/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-indigo-600">{stagePrediction.next_stage_probability}%</p>
                  <p className="text-xs text-indigo-700">Next Stage</p>
                </div>
                <div className="bg-white/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-emerald-600">{stagePrediction.booking_probability}%</p>
                  <p className="text-xs text-emerald-700">Booking</p>
                </div>
                <div className="bg-white/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-red-600">{stagePrediction.dropoff_probability}%</p>
                  <p className="text-xs text-red-700">Drop-off Risk</p>
                </div>
                <div className="bg-white/50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-slate-700">{stagePrediction.days_to_next_stage}</p>
                  <p className="text-xs text-slate-600">Days to Next</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {stagePrediction.risk_factors?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-600 mb-1">Risk Factors:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      {stagePrediction.risk_factors.map((r, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span>•</span> {r}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {stagePrediction.positive_signals?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-emerald-600 mb-1">Positive Signals:</p>
                    <ul className="text-sm text-emerald-700 space-y-1">
                      {stagePrediction.positive_signals.map((s, i) => (
                        <li key={i} className="flex items-start gap-1">
                          <span>•</span> {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {stagePrediction.recommendation && (
                <div className="mt-3 p-2 bg-white/50 rounded-lg flex items-start justify-between gap-2">
                  <p className="text-sm text-indigo-800">
                    <strong>Recommendation:</strong> {stagePrediction.recommendation}
                  </p>
                  <CopyButton text={stagePrediction.recommendation} label="stage-recommendation" />
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Project Fit Card */}
        {projectFit && (
          <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200" data-testid="ai-project-fit-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center">
                    <Target className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-emerald-800">Project Recommendation</p>
                    <p className="text-lg font-bold text-emerald-900">{projectFit.recommended_project}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600">{projectFit.fit_score}%</p>
                  <p className="text-xs text-emerald-700">Fit Score</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs font-medium text-emerald-600 mb-1">Why This Project Fits:</p>
                  <ul className="text-sm text-emerald-800 space-y-1">
                    {projectFit.reasons?.map((r, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span>✓</span> {r}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-amber-600 mb-1">Concerns to Address:</p>
                  <ul className="text-sm text-amber-800 space-y-1">
                    {projectFit.concerns?.map((c, i) => (
                      <li key={i} className="flex items-start gap-1">
                        <span>!</span> {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {projectFit.pitch_talking_points?.length > 0 && (
                <div className="bg-white/50 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-emerald-600">Talking Points:</p>
                    <CopyButton text={projectFit.pitch_talking_points.join("\n")} label="project-talking-points" />
                  </div>
                  <ul className="text-sm text-emerald-800">
                    {projectFit.pitch_talking_points.map((p, i) => (
                      <li key={i}>• {p}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex items-center gap-2">
                <AIFeedback 
                  feature="project_fit"
                  leadId={leadId}
                  aiResponse={projectFit}
                  compact={true}
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                  onClick={() => setProjectFit(null)}
                >
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Next Action Card (Awareness-based) - always visible if awareness exists */}
        {lead.awareness_stage && (
          <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200" data-testid="next-action-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${getAwarenessBgColor(lead.awareness_stage)}`}>
                    <Zap className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-amber-800">Next Action</p>
                    <p className="text-xs text-amber-600">
                      {lead.awareness_stage} • {lead.awareness_confidence} confidence
                      {lead.awareness_buyer_segment && ` • ${lead.awareness_buyer_segment.replace(/_/g, " ")}`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <AIFeedback 
                    feature="next_action"
                    leadId={leadId}
                    aiResponse={{
                      stage: lead.awareness_stage,
                      summary: lead.awareness_summary,
                      next_belief: lead.awareness_next_belief,
                      action: lead.awareness_action
                    }}
                    compact={true}
                  />
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    onClick={runAwarenessAnalysis}
                    disabled={runningAwareness}
                    data-testid="rerun-next-action-btn"
                  >
                    {runningAwareness ? <RefreshCw className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                    Refresh
                  </Button>
                </div>
              </div>
              
              {/* Recommended Action - highlighted at top */}
              {lead.awareness_action && (
                <div className="bg-amber-100 border border-amber-200 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-amber-700 mb-1">Recommended Action:</p>
                  <p className="text-sm font-semibold text-amber-900">{lead.awareness_action}</p>
                </div>
              )}

              {lead.awareness_summary && (
                <div className="bg-white/50 rounded-lg p-3 mb-3">
                  <p className="text-xs font-medium text-amber-600 mb-1">Buyer Mindset:</p>
                  <p className="text-sm text-amber-800">{lead.awareness_summary}</p>
                </div>
              )}

              {lead.awareness_next_belief && (
                <div className="bg-white/30 rounded-lg p-2 mb-3">
                  <p className="text-xs font-medium text-amber-600 mb-1">Next Belief Shift Needed:</p>
                  <p className="text-sm text-amber-700">{lead.awareness_next_belief}</p>
                </div>
              )}

              {lead.awareness_evidence && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <p className="text-xs text-amber-500">
                    Evidence: {lead.awareness_evidence}
                  </p>
                </div>
              )}
              {lead.awareness_analyzed_at && (
                <p className="text-xs text-amber-400 mt-2">
                  Last analyzed: {new Date(lead.awareness_analyzed_at).toLocaleString()}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* MhM Sales Coaching */}
        <MhMSalesCoaching apiBase={API} leadId={leadId} />

        {/* Buyer Context (custom_fields from external sources e.g. mhm_lite) */}
        {lead.custom_fields && Object.keys(lead.custom_fields).length > 0 && (
          <Card className="bg-gradient-to-br from-violet-50 to-fuchsia-50 border-violet-200" data-testid="buyer-context-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-violet-500 rounded-lg flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-violet-900">Buyer Context</p>
                  <p className="text-xs text-violet-600">From external lead capture</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {Object.entries(lead.custom_fields).map(([k, v]) => (
                  v !== null && v !== "" ? (
                    <div key={k} className="bg-white/70 rounded-lg p-2.5 border border-violet-100" data-testid={`buyer-context-${k}`}>
                      <p className="text-[11px] uppercase tracking-wide text-violet-500 font-medium mb-0.5">
                        {String(k).replace(/_/g, " ")}
                      </p>
                      <p className="text-sm text-violet-900 font-medium">
                        {typeof v === "object" ? JSON.stringify(v) : String(v)}
                      </p>
                    </div>
                  ) : null
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Nurture Journey Card */}
        <NurtureJourneyCard apiBase={API} leadId={leadId} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Lead Info */}
          <div className="space-y-6">
            {/* Contact Card */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                    <Phone className="w-5 h-5 text-slate-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium">{lead.phone}</p>
                  </div>
                </div>
                {lead.email && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Mail className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email</p>
                      <p className="font-medium">{lead.email}</p>
                    </div>
                  </div>
                )}
                {lead.locality_preference && (
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Location Preference</p>
                      <p className="font-medium">{lead.locality_preference}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Linked Leads */}
            {linkedLeads.length > 0 && (
              <Card className="bg-white border-blue-200 shadow-sm" data-testid="linked-leads-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-blue-600" />
                    Linked Leads
                    <Badge variant="secondary" className="text-xs">{linkedLeads.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {linkedLeads.map(ll => (
                    <div 
                      key={ll.lead_id}
                      onClick={() => navigate(`/leads/${ll.lead_id}`)}
                      className="flex items-center justify-between p-2.5 rounded-lg bg-blue-50/50 border border-blue-100 cursor-pointer hover:bg-blue-100/50 transition-colors"
                      data-testid={`linked-lead-${ll.lead_id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 truncate">{ll.project_interest || "No project"}</p>
                        <p className="text-xs text-slate-500">{ll.source} &middot; {ll.stage}</p>
                      </div>
                      <Badge className={`text-[10px] shrink-0 ${SCORE_COLORS[ll.score] || ""}`}>{ll.score}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Re-enquiry History */}
            {lead.reinquiry_history?.length > 0 && (
              <Card className="bg-white border-amber-200 shadow-sm" data-testid="reinquiry-history-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
                    <Activity className="w-4 h-4 text-amber-600" />
                    Re-enquiry Trail
                    <Badge variant="secondary" className="text-xs">{lead.reinquiry_history.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {lead.reinquiry_history.map((rq, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-amber-50/50 border border-amber-100">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800 capitalize">{rq.source?.replace(/_/g, ' ')}</p>
                        {rq.campaign && <p className="text-xs text-slate-500">Campaign: {rq.campaign}</p>}
                      </div>
                      <span className="text-xs text-slate-400 shrink-0">{(rq.timestamp || rq.date) ? new Date(rq.timestamp || rq.date).toLocaleDateString() : ''}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Referrer Info */}
            {referralInfo && referralInfo.referrer && (
              <Card className="bg-white border-teal-200 shadow-sm" data-testid="referrer-info-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
                    <UsersRound className="w-4 h-4 text-teal-600" />
                    Referred By
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="p-3 bg-teal-50/50 rounded-lg border border-teal-100">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-slate-800">{referralInfo.referrer.name}</p>
                      <Badge variant="outline" className="text-[10px] capitalize">
                        {referralInfo.referrer.type?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div className="flex gap-3 mt-1.5 text-xs text-slate-500">
                      {referralInfo.referrer.phone && (
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{referralInfo.referrer.phone}</span>
                      )}
                      {referralInfo.referrer.email && (
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{referralInfo.referrer.email}</span>
                      )}
                    </div>
                  </div>
                  {/* Commission info */}
                  {(referralInfo.commission_percentage || referralInfo.commission_amount) && (
                    <div className="flex items-center gap-3 text-xs">
                      <span className="text-slate-500">Commission:</span>
                      {referralInfo.commission_percentage && (
                        <Badge variant="secondary" className="text-[10px]">{referralInfo.commission_percentage}%</Badge>
                      )}
                      {referralInfo.commission_amount && (
                        <span className="font-medium text-slate-700">{Number(referralInfo.commission_amount).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</span>
                      )}
                      <Badge variant="outline" className={`text-[10px] capitalize ${
                        referralInfo.commission_status === 'paid' ? 'text-emerald-600 border-emerald-200' :
                        referralInfo.commission_status === 'approved' ? 'text-blue-600 border-blue-200' :
                        'text-amber-600 border-amber-200'
                      }`}>
                        {referralInfo.commission_status}
                      </Badge>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* EOI History Card */}
            {leadEois.length > 0 && (
              <Card className="bg-white border-purple-200 shadow-sm" data-testid="lead-eois-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base font-semibold font-['Manrope'] flex items-center gap-2">
                    <FileText className="w-4 h-4 text-purple-600" />
                    EOI History
                    <Badge variant="secondary" className="text-xs">{leadEois.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {leadEois.map((eoi) => (
                    <div 
                      key={eoi.eoi_id}
                      onClick={() => navigate('/eoi')}
                      className="p-2.5 rounded-lg bg-purple-50/50 border border-purple-100 cursor-pointer hover:bg-purple-100/50 transition-colors"
                      data-testid={`lead-eoi-${eoi.eoi_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{eoi.project_name || "Project"}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs text-slate-500">Token: ₹{Number(eoi.token_amount || 0).toLocaleString()}</span>
                            <span className="text-xs text-slate-400">•</span>
                            <span className="text-xs text-slate-500">Priority #{eoi.priority_number}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          <Badge className={`text-[10px] ${
                            eoi.status === 'active' ? 'bg-emerald-100 text-emerald-700' :
                            eoi.status === 'converted' ? 'bg-blue-100 text-blue-700' :
                            eoi.status === 'refunded' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {eoi.status}
                          </Badge>
                          {eoi.linked_eois?.length > 0 && (
                            <span className="flex items-center gap-0.5 text-[10px] text-purple-500">
                              <Link2 className="w-3 h-3" />
                              {eoi.linked_eois.length} linked
                            </span>
                          )}
                        </div>
                      </div>
                      {eoi.eoi_date && (
                        <p className="text-[10px] text-slate-400 mt-1">
                          {new Date(eoi.eoi_date).toLocaleDateString()}
                          {eoi.source && ` • Via ${eoi.source === 'cp_portal' ? 'Channel Partner' : 'In-house'}`}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Requirements Card */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Requirements</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <IndianRupee className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Budget Range</p>
                    <p className="font-medium">{formatINR(lead.budget_min)} - {formatINR(lead.budget_max)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Configuration</p>
                    <p className="font-medium">{lead.configuration || 'Not specified'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Project Interest</p>
                    <p className="font-medium">{lead.project_interest || 'Not specified'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stage & Assignment */}
            <Card className="bg-white border-slate-200 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Status</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-slate-500">Stage</Label>
                  <Select value={lead.stage} onValueChange={handleStageChange} disabled={!canEditThisLead}>
                    <SelectTrigger className="mt-1" data-testid="stage-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="qualified">Qualified</SelectItem>
                      <SelectItem value="site_visit_scheduled">Site Visit Scheduled</SelectItem>
                      <SelectItem value="site_visit">Site Visit</SelectItem>
                      <SelectItem value="revisit">Revisit</SelectItem>
                      <SelectItem value="negotiation_scheduled">Negotiation Scheduled</SelectItem>
                      <SelectItem value="negotiation">Negotiation</SelectItem>
                      <SelectItem value="booked">Booked</SelectItem>
                      <SelectItem value="re_engaged">Re-Engaged</SelectItem>
                      <SelectItem value="lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Lost Reason Display */}
                {lead.stage === "lost" && lead.lost_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 space-y-1" data-testid="lost-reason-display">
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">Lost Reason</p>
                    <p className="text-sm font-medium text-slate-900 capitalize">{lead.lost_reason?.replace(/_/g, ' ')}</p>
                    {lead.lost_sub_reason && <p className="text-xs text-slate-600">{lead.lost_sub_reason}</p>}
                    {lead.lost_notes && <p className="text-xs text-slate-500 italic">{lead.lost_notes}</p>}
                    {lead.lost_date && <p className="text-[10px] text-red-400">Lost on {new Date(lead.lost_date).toLocaleDateString('en-IN')}</p>}
                  </div>
                )}
                <div>
                  <Label className="text-sm text-slate-500">Assigned To</Label>
                  <Select value={lead.assigned_to || ""} onValueChange={handleAssignmentChange} disabled={!canEditThisLead}>
                    <SelectTrigger className="mt-1" data-testid="assignment-select">
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Source (First Touch)</p>
                  <p className="font-medium capitalize mt-1">{lead.source?.replace(/_/g, ' ')}</p>
                </div>
                {referralInfo && referralInfo.referrer && (lead.source === 'referral' || lead.source === 'channel_partner') && (
                  <div data-testid="source-referrer-display">
                    <p className="text-sm text-slate-500">Referrer</p>
                    <p className="font-medium mt-1 text-teal-600">{referralInfo.referrer.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5 capitalize">{referralInfo.referrer.type?.replace(/_/g, ' ')}</p>
                  </div>
                )}
                {lead.latest_source && (
                  <div data-testid="latest-source-display">
                    <p className="text-sm text-slate-500">Latest Source</p>
                    <p className="font-medium capitalize mt-1 text-blue-600">{lead.latest_source?.replace(/_/g, ' ')}</p>
                  </div>
                )}
                <div data-testid="campaign-display">
                  <p className="text-sm text-slate-500">Campaign</p>
                  {lead.latest_campaign ? (
                    <div className="flex items-center gap-2 mt-1">
                      <p className="font-medium text-purple-600">{lead.latest_campaign}</p>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="h-6 w-6 p-0 text-slate-400 hover:text-purple-600"
                        onClick={() => { setSelectedCampaign(lead.campaign_id || ""); setCampaignDialogOpen(true); }}
                        data-testid="change-campaign-btn"
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="mt-1 text-xs text-purple-600 border-purple-200 hover:bg-purple-50"
                      onClick={() => setCampaignDialogOpen(true)}
                      data-testid="tag-campaign-btn"
                    >
                      <Plus className="w-3 h-3 mr-1" /> Tag to Campaign
                    </Button>
                  )}
                </div>
                {lead.reinquiry_count > 0 && (
                  <div data-testid="reinquiry-count-display">
                    <p className="text-sm text-slate-500">Re-enquiries</p>
                    <p className="font-medium mt-1 text-amber-600">{lead.reinquiry_count} re-enquiry(s)</p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-500">AI Score</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${lead.score === 'hot' ? 'bg-red-500' : lead.score === 'warm' ? 'bg-amber-500' : 'bg-blue-500'}`}
                        style={{ width: `${lead.score_value || 50}%` }}
                      ></div>
                    </div>
                    <span className="text-sm font-medium">{lead.score_value || 50}/100</span>
                  </div>
                </div>
                {(lead.stage === "revisit" || lead.revisit_count > 0) && (
                  <div data-testid="revisit-count-display">
                    <p className="text-sm text-slate-500">Revisit Count</p>
                    <p className="font-medium mt-1 text-fuchsia-600">{lead.revisit_count || 0} revisit(s)</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* IVR Call Data Card */}
            {lead.ivr_data && (
              <Card className="bg-white border-slate-200 shadow-sm border-l-4 border-l-orange-400" data-testid="ivr-data-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope'] flex items-center gap-2">
                    <Phone className="w-5 h-5 text-orange-500" /> IVR Call Data
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {lead.ivr_data.call_duration != null && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Call Duration</p>
                      <p className="font-medium text-slate-800">
                        {Math.floor(lead.ivr_data.call_duration / 60)}m {lead.ivr_data.call_duration % 60}s
                      </p>
                    </div>
                  )}
                  {lead.ivr_data.ivr_timestamp && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">Call Time</p>
                      <p className="font-medium text-slate-800">{new Date(lead.ivr_data.ivr_timestamp).toLocaleString()}</p>
                    </div>
                  )}
                  {lead.ivr_data.ivr_option && (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-slate-500">IVR Selection</p>
                      <p className="font-medium text-slate-800">{lead.ivr_data.ivr_option}</p>
                    </div>
                  )}
                  {lead.ivr_data.recording_url && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Call Recording</p>
                      <a
                        href={lead.ivr_data.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 bg-orange-50 px-3 py-1.5 rounded-md"
                        data-testid="ivr-recording-link"
                      >
                        <Phone className="w-3.5 h-3.5" /> Listen to Recording
                      </a>
                    </div>
                  )}
                  {lead.ivr_data.transcript && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1">Transcript</p>
                      <div className="bg-slate-50 rounded-md p-3 text-sm text-slate-700 max-h-40 overflow-y-auto" data-testid="ivr-transcript">
                        {lead.ivr_data.transcript}
                      </div>
                    </div>
                  )}
                  {lead.needs_completion && (
                    <div className="bg-amber-50 border border-amber-200 rounded-md px-3 py-2 text-sm text-amber-700 font-medium" data-testid="ivr-needs-completion">
                      Lead details incomplete — please fill in missing information above.
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Enrichment Scores Dashboard */}
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="scores-dashboard">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold font-['Manrope']">Lead Scores</CardTitle>
                  <p className="text-xs text-slate-400 mt-0.5">Engagement = call/visit/chat activity. Intent = budget match, config & site visits. Readiness = overall buying probability.</p>
                </div>
                <Button size="sm" variant="outline" onClick={handleRecalculateScores} disabled={calculatingScores} data-testid="recalculate-scores-btn">
                  {calculatingScores ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Gauge className="w-3 h-3" />}
                  <span className="ml-1 text-xs">Recalculate</span>
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Engagement", key: "engagement_score", color: "bg-blue-500", textColor: "text-blue-600", desc: "Activity & interactions" },
                  { label: "Intent", key: "intent_score", color: "bg-violet-500", textColor: "text-violet-600", desc: "Budget, config & visits" },
                  { label: "Readiness", key: "readiness_score", color: "bg-emerald-500", textColor: "text-emerald-600", desc: "How ready to buy" },
                ].map((s) => {
                  const val = enrichment?.scores?.[s.label.toLowerCase()]?.[s.key] ?? lead?.[s.key] ?? 0;
                  return (
                    <div key={s.key} data-testid={`score-${s.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm text-slate-600">{s.label}</span>
                        <span className={`text-sm font-bold ${s.textColor}`}>{val}/100</span>
                      </div>
                      <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${s.color} transition-all`} style={{ width: `${val}%` }} />
                      </div>
                      <p className="text-[10px] text-slate-400 mt-0.5">{s.desc}</p>
                    </div>
                  );
                })}
                {lead?.readiness_label && (
                  <div className="text-center pt-1">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      lead.readiness_label === 'ready_to_buy' ? 'bg-emerald-100 text-emerald-700' :
                      lead.readiness_label === 'warming_up' ? 'bg-amber-100 text-amber-700' :
                      lead.readiness_label === 'exploring' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-500'
                    }`} data-testid="readiness-label">
                      {lead.readiness_label?.replace(/_/g, ' ')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Family / Decision Groups */}
            <Card className="bg-white border-slate-200 shadow-sm" data-testid="family-groups-panel">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-semibold font-['Manrope']">Decision Group</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => navigate('/family-groups')} data-testid="manage-groups-link">
                  <UsersRound className="w-3 h-3 mr-1" /> Manage
                </Button>
              </CardHeader>
              <CardContent>
                {familyGroups.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-3">Not part of any group</p>
                ) : (
                  <div className="space-y-3">
                    {familyGroups.map((group) => (
                      <div key={group.group_id} className="border rounded-lg p-2.5 space-y-2">
                        <div className="flex items-center gap-2">
                          <Heart className="w-3.5 h-3.5 text-rose-500" />
                          <span className="text-sm font-medium text-slate-800">{group.name}</span>
                          <span className="text-[10px] text-slate-400 capitalize">{group.relationship_type?.replace(/_/g, ' ')}</span>
                        </div>
                        {(group.other_members || group.members || [])
                          .filter(m => m.lead_id !== leadId)
                          .map((m) => (
                            <div key={m.lead_id} className="flex items-center justify-between text-xs bg-slate-50 rounded px-2 py-1">
                              <button className="text-blue-600 hover:underline" onClick={() => navigate(`/leads/${m.lead_id}`)}>
                                {m.name}
                              </button>
                              <span className="text-slate-400 capitalize">{m.stage}</span>
                            </div>
                          ))}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Activity */}
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="w-full h-auto flex overflow-x-auto gap-1 p-1 mb-4">
                <TabsTrigger value="interactions" data-testid="interactions-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Interactions ({interactionsTotal})</TabsTrigger>
                <TabsTrigger value="comms" data-testid="comms-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Communications ({whatsappMessages.length + smsMessages.length + emailMessages.length})</TabsTrigger>
                <TabsTrigger value="tasks" data-testid="tasks-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Tasks ({tasks.length})</TabsTrigger>
                <TabsTrigger value="visits" data-testid="visits-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Visits ({visits.length})</TabsTrigger>
                <TabsTrigger value="routing" data-testid="routing-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Routing</TabsTrigger>
                <TabsTrigger value="tools" data-testid="tools-tab" className="text-xs whitespace-nowrap px-2 py-2 flex-shrink-0">Tools</TabsTrigger>
              </TabsList>

              <TabsContent value="interactions">
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Interactions</CardTitle>
                    <Dialog open={showInteractionDialog} onOpenChange={setShowInteractionDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="add-interaction-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Add Interaction</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <DynamicFormRenderer
                            formType="interaction"
                            values={newInteraction}
                            onChange={(field, value) => setNewInteraction(prev => ({ ...prev, [field]: value }))}
                            showGroups={false}
                            gridCols={1}
                          />
                          <Button onClick={handleAddInteraction} className="w-full" data-testid="save-interaction-btn">Save Interaction</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    {/* Type filter chips (only shown when 2+ types exist) */}
                    {interactionsTypes.length > 1 && (
                      <div className="flex flex-wrap gap-1.5 mb-3 pb-3 border-b border-slate-100" data-testid="interactions-type-filter">
                        <button
                          onClick={() => applyInteractionFilter("all")}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${interactionsFilter === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                          data-testid="filter-chip-all"
                        >
                          All ({interactionsTypes.reduce((s, t) => s + t.count, 0)})
                        </button>
                        {interactionsTypes.slice(0, 12).map(t => (
                          <button
                            key={t.type}
                            onClick={() => applyInteractionFilter(t.type)}
                            className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${interactionsFilter === t.type ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
                            data-testid={`filter-chip-${t.type}`}
                            title={t.type.replace(/_/g, " ")}
                          >
                            {t.type.replace(/_/g, " ")} ({t.count})
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="space-y-4">
                      {interactions.map((int) => (
                        <div key={int.interaction_id} className="border-l-2 border-slate-200 pl-4 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium uppercase text-slate-500">{int.subtype || int.type}</span>
                            {int.sentiment && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${int.sentiment === 'positive' ? 'bg-green-100 text-green-700' : int.sentiment === 'negative' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                                {int.sentiment}
                              </span>
                            )}
                            <span className="text-xs text-slate-400">
                              {new Date(int.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-slate-700">{int.content || int.notes}</p>
                          {int.summary && (
                            <p className="text-sm text-slate-500 mt-2 italic bg-slate-50 p-2 rounded">
                              AI Summary: {stripMarkdown(int.summary)}
                            </p>
                          )}
                        </div>
                      ))}
                      {interactions.length === 0 && (
                        <p className="text-center text-slate-500 py-8">
                          {interactionsFilter !== "all" ? `No "${interactionsFilter.replace(/_/g, ' ')}" interactions` : "No interactions yet"}
                        </p>
                      )}
                      {interactions.length < interactionsTotal && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={loadMoreInteractions}
                          disabled={interactionsLoadingMore}
                          className="w-full mt-2"
                          data-testid="load-more-interactions-btn"
                        >
                          {interactionsLoadingMore
                            ? "Loading…"
                            : `Load ${Math.min(30, interactionsTotal - interactions.length)} more (${interactionsTotal - interactions.length} remaining)`}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* WhatsApp Tab */}
              <TabsContent value="comms">
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Communications</CardTitle>
                    <div className="flex gap-2">
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8 text-xs" onClick={() => openSendDialog("whatsapp")} data-testid="send-whatsapp-btn">
                        <Send className="w-3.5 h-3.5 mr-1" /> WhatsApp
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-blue-300 text-blue-700 hover:bg-blue-50" onClick={() => openSendDialog("sms")} data-testid="send-sms-btn">
                        <Phone className="w-3.5 h-3.5 mr-1" /> SMS
                      </Button>
                      <Button size="sm" variant="outline" className="h-8 text-xs border-purple-300 text-purple-700 hover:bg-purple-50" onClick={() => openSendDialog("email")} data-testid="send-email-btn">
                        <Mail className="w-3.5 h-3.5 mr-1" /> Email
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {[
                        ...whatsappMessages.map(m => ({...m, channel: "whatsapp"})),
                        ...smsMessages.map(m => ({...m, channel: "sms"})),
                        ...emailMessages.map(m => ({...m, channel: "email"})),
                      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((msg) => (
                        <div 
                          key={msg.message_id} 
                          className={`p-3 rounded-lg max-w-[85%] ${
                            msg.direction === 'outbound' ? 'ml-auto' : ''
                          } ${
                            msg.channel === "whatsapp" ? (msg.direction === "outbound" ? "bg-green-50 border border-green-200" : "bg-slate-100") :
                            msg.channel === "sms" ? (msg.direction === "outbound" ? "bg-blue-50 border border-blue-200" : "bg-slate-100") :
                            (msg.direction === "outbound" ? "bg-purple-50 border border-purple-200" : "bg-slate-100")
                          }`}
                        >
                          {msg.subject && <p className="text-xs font-medium text-slate-700 mb-1">Subject: {msg.subject}</p>}
                          <p className="text-sm text-slate-800 whitespace-pre-wrap">{msg.content}</p>
                          <div className="flex items-center justify-between mt-2 text-xs text-slate-500">
                            <div className="flex items-center gap-2">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium uppercase ${
                                msg.channel === "whatsapp" ? "bg-green-100 text-green-700" :
                                msg.channel === "sms" ? "bg-blue-100 text-blue-700" :
                                "bg-purple-100 text-purple-700"
                              }`}>{msg.channel}</span>
                              <span>{new Date(msg.created_at).toLocaleString()}</span>
                            </div>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                              msg.status === 'sent' || msg.status === 'delivered' ? 'bg-green-200 text-green-700' :
                              msg.status === 'failed' ? 'bg-red-200 text-red-700' :
                              'bg-amber-200 text-amber-700'
                            }`}>{msg.status}</span>
                          </div>
                        </div>
                      ))}
                      {whatsappMessages.length + smsMessages.length + emailMessages.length === 0 && (
                        <p className="text-center text-slate-400 py-8">No messages sent yet. Use the buttons above to send via WhatsApp, SMS, or Email.</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Unified Send Message Dialog */}
              <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-['Manrope'] flex items-center gap-2">
                      {msgChannel === "whatsapp" ? <Send className="w-4 h-4 text-green-600" /> :
                       msgChannel === "sms" ? <Phone className="w-4 h-4 text-blue-600" /> :
                       <Mail className="w-4 h-4 text-purple-600" />}
                      Send {msgChannel === "whatsapp" ? "WhatsApp" : msgChannel === "sms" ? "SMS" : "Email"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                      <span className="text-slate-500">To:</span> <span className="font-medium">{lead?.name}</span>
                      {msgChannel === "email" ? (
                        <span className="text-slate-500 ml-1">({lead?.email || "No email"})</span>
                      ) : (
                        <span className="text-slate-500 ml-1">({lead?.phone || "No phone"})</span>
                      )}
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Select Template</Label>
                      <Select value={selectedMsgTemplate} onValueChange={setSelectedMsgTemplate}>
                        <SelectTrigger className="mt-1" data-testid="msg-template-select">
                          <SelectValue placeholder="Choose an approved template..." />
                        </SelectTrigger>
                        <SelectContent>
                          {channelTemplates.length === 0 ? (
                            <div className="px-3 py-2 text-xs text-slate-400">No approved templates for {msgChannel}.</div>
                          ) : channelTemplates.map(t => (
                            <SelectItem key={t.template_id} value={t.template_id}>
                              <span className="flex items-center gap-2">
                                <span className="capitalize text-[10px] text-slate-400">{t.category?.replace(/_/g, " ")}</span>
                                <span>{t.name}</span>
                                {t.doubletick_template_name && <span className="text-[9px] bg-green-100 text-green-700 px-1 py-0.5 rounded">DoubleTick</span>}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {selectedMsgTemplate && (() => {
                      const sel = channelTemplates.find(t => t.template_id === selectedMsgTemplate);
                      if (!sel) return null;
                      const resolved = getResolvedPreview(sel);
                      return (
                        <div className={`rounded-xl p-4 text-sm leading-relaxed whitespace-pre-wrap border ${
                          msgChannel === "whatsapp" ? "bg-green-50 border-green-200" :
                          msgChannel === "sms" ? "bg-blue-50 border-blue-200" :
                          "bg-purple-50 border-purple-200"
                        }`}>
                          <p className="text-[10px] font-medium text-slate-500 mb-2 uppercase tracking-wider">Preview (with lead data)</p>
                          {sel.subject && <p className="font-medium text-xs mb-2 pb-1 border-b border-current/10">Subject: {getResolvedPreview({...sel, content: sel.subject})}</p>}
                          {resolved}
                          {sel.variables?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-3 pt-2 border-t border-current/10">
                              {sel.variables.map(v => (
                                <span key={v} className="px-1.5 py-0.5 rounded bg-white/60 text-[9px] font-mono">{`{${v}}`}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    <Button
                      onClick={handleSendMessage}
                      disabled={!selectedMsgTemplate || sendingMessage}
                      className={`w-full ${
                        msgChannel === "whatsapp" ? "bg-green-600 hover:bg-green-700" :
                        msgChannel === "sms" ? "bg-blue-600 hover:bg-blue-700" :
                        "bg-purple-600 hover:bg-purple-700"
                      }`}
                      data-testid="send-message-btn"
                    >
                      {sendingMessage ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                      ) : (
                        <><Send className="w-4 h-4 mr-2" /> Send {msgChannel === "whatsapp" ? "WhatsApp" : msgChannel === "sms" ? "SMS" : "Email"}</>
                      )}
                    </Button>

                    <div className="flex items-center justify-between">
                      <p className="text-[10px] text-slate-400">
                        Message will be queued. Connect your provider for live delivery.
                      </p>
                      <a href="/message-templates" target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline">
                        Manage Templates
                      </a>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>

              <TabsContent value="tasks">
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Tasks</CardTitle>
                    <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
                      <DialogTrigger asChild>
                        <Button size="sm" data-testid="add-task-btn">
                          <Plus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Create Task</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <Label>Title <span className="text-slate-400 text-xs">(optional — auto-generated if blank)</span></Label>
                            <Input
                              placeholder="e.g. Follow up about RERA approval"
                              value={newTask.title}
                              onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                              data-testid="task-title-input"
                            />
                          </div>
                          <div>
                            <Label>Type</Label>
                            <Select value={newTask.task_type} onValueChange={(v) => setNewTask({...newTask, task_type: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="call">Call</SelectItem>
                                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                                <SelectItem value="face_to_face">Face to Face Meeting</SelectItem>
                                <SelectItem value="email">Email</SelectItem>
                                <SelectItem value="document">Document</SelectItem>
                                <SelectItem value="follow_up">Follow Up</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Due Date</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal">
                                  <Calendar className="mr-2 h-4 w-4" />
                                  {format(newTask.due_date, "PPP")}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0">
                                <CalendarComponent
                                  mode="single"
                                  selected={newTask.due_date}
                                  onSelect={(date) => setNewTask({...newTask, due_date: date || new Date()})}
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label>Priority</Label>
                            <Select value={newTask.priority} onValueChange={(v) => setNewTask({...newTask, priority: v})}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="high">High</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="low">Low</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Description</Label>
                            <Textarea
                              placeholder="Task description..."
                              value={newTask.description}
                              onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                              rows={3}
                            />
                          </div>
                          <Button onClick={handleAddTask} className="w-full" data-testid="create-task-btn">Create Task</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {tasks.map((task) => (
                        <div key={task.task_id} className={`p-3 rounded-lg border ${task.status === 'completed' ? 'bg-green-50 border-green-200' : task.status === 'overdue' ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`} data-testid={`task-row-${task.task_id}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium ${task.status === 'completed' ? 'line-through text-slate-400' : ''}`}>{task.title}</p>
                              <p className="text-sm text-slate-500">
                                {task.task_type?.replace(/_/g, ' ')} • Due: {new Date(task.due_date).toLocaleDateString()}
                              </p>
                              {task.description && (
                                <p className="text-xs text-slate-500 mt-1 line-clamp-2">{task.description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className={`text-xs px-2 py-1 rounded-full ${task.priority === 'high' ? 'bg-red-100 text-red-700' : task.priority === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                {task.priority}
                              </span>
                              {task.status !== 'completed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                                  onClick={() => {
                                    setCompleteDialog({ open: true, task });
                                    setCompleteOutcome("");
                                    setCompleteNotes("");
                                  }}
                                  data-testid={`complete-task-${task.task_id}`}
                                >
                                  <Check className="w-4 h-4 mr-1" /> Done
                                </Button>
                              )}
                              {task.status !== 'completed' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                                  onClick={() => {
                                    setVoiceLogDefaultType("task_completion_note");
                                    setVoiceLogTaskId(task.task_id);
                                    setVoiceLogSiteVisitId(null);
                                    setVoiceLogOpen(true);
                                  }}
                                  data-testid={`voice-done-task-${task.task_id}`}
                                  title="Done with Voice Note"
                                >
                                  <Mic className="w-4 h-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-500 hover:text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteTask(task.task_id)}
                                data-testid={`delete-task-${task.task_id}`}
                                title="Delete task"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {tasks.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No tasks yet</p>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Task Completion Outcome Dialog */}
                <Dialog open={completeDialog.open} onOpenChange={(o) => setCompleteDialog({ ...completeDialog, open: o })}>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Mark task done</DialogTitle>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-1">{completeDialog.task?.title}</p>
                    </DialogHeader>
                    <div className="space-y-4 mt-2">
                      <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">What happened?</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          {[
                            { key: "connected", label: "Connected", color: "emerald" },
                            { key: "no_answer", label: "No answer", color: "amber" },
                            { key: "rescheduled", label: "Rescheduled", color: "blue" },
                            { key: "not_interested", label: "Not interested", color: "rose" },
                          ].map((opt) => (
                            <Button
                              key={opt.key}
                              variant={completeOutcome === opt.key ? "default" : "outline"}
                              size="sm"
                              onClick={() => setCompleteOutcome(opt.key)}
                              data-testid={`outcome-${opt.key}-btn`}
                              className={completeOutcome === opt.key
                                ? `bg-${opt.color}-600 hover:bg-${opt.color}-700 text-white`
                                : `border-${opt.color}-200 text-${opt.color}-700 hover:bg-${opt.color}-50`}
                            >
                              {opt.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wide text-slate-500">Note (optional)</Label>
                        <Textarea
                          placeholder="e.g. Wants to revisit next week, asked about loan eligibility"
                          rows={2}
                          value={completeNotes}
                          onChange={(e) => setCompleteNotes(e.target.value)}
                          data-testid="outcome-notes-input"
                          className="mt-1"
                        />
                        <p className="text-[11px] text-slate-400 mt-1">This auto-logs as an interaction in the lead's history.</p>
                      </div>
                      <div className="flex justify-end gap-2 pt-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCompleteDialog({ open: false, task: null })}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            await handleCompleteTask(completeDialog.task?.task_id, "", "");
                            setCompleteDialog({ open: false, task: null });
                          }}
                          data-testid="complete-skip-btn"
                        >
                          Skip — just mark done
                        </Button>
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          disabled={!completeOutcome}
                          onClick={async () => {
                            await handleCompleteTask(completeDialog.task?.task_id, completeOutcome, completeNotes);
                            setCompleteDialog({ open: false, task: null });
                          }}
                          data-testid="complete-submit-btn"
                        >
                          <Check className="w-4 h-4 mr-1" /> Confirm
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </TabsContent>

              <TabsContent value="visits">
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Site Visits</CardTitle>
                    <div className="flex items-center gap-2">
                      {/* Prep Visit button - shown when there are scheduled visits */}
                      {visits.some(v => ["scheduled", "confirmed"].includes(v.status)) && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-violet-600 border-violet-200 hover:bg-violet-50"
                          onClick={() => setVisitPrepOpen(true)}
                          data-testid="prep-visit-btn"
                        >
                          <MapPin className="w-4 h-4 mr-1" /> Prep Visit
                        </Button>
                      )}
                      {visits.some(v => v.status === "completed") && (
                        <>
                        <Dialog open={showRevisitDialog} onOpenChange={setShowRevisitDialog}>
                          <DialogTrigger asChild>
                            <Button size="sm" variant="outline" className="text-fuchsia-600 border-fuchsia-200 hover:bg-fuchsia-50" data-testid="schedule-revisit-btn">
                              <RefreshCw className="w-4 h-4 mr-1" /> Schedule Revisit
                            </Button>
                          </DialogTrigger>
                          <DialogContent>
                            <DialogHeader>
                              <DialogTitle className="font-['Manrope']">Schedule Revisit</DialogTitle>
                            </DialogHeader>
                            <p className="text-sm text-slate-500 mb-2">Schedule a follow-up site visit for <span className="font-medium text-slate-700">{lead?.name}</span>. The lead stage will be updated to Revisit.</p>
                            <div className="space-y-4">
                              <DynamicFormRenderer
                                formType="revisit"
                                values={newVisit}
                                onChange={(field, value) => setNewVisit(prev => ({ ...prev, [field]: value }))}
                                projects={projects}
                                showGroups={false}
                                gridCols={1}
                              />
                              <Button onClick={handleScheduleRevisit} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="confirm-revisit-lead-btn">
                                <RefreshCw className="w-4 h-4 mr-2" /> Schedule Revisit
                              </Button>
                            </div>
                          </DialogContent>
                        </Dialog>
                      <Button
                          size="sm"
                          variant="outline"
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                          data-testid="log-unscheduled-revisit-btn"
                          onClick={() => { setDebriefVisitId(null); setDebriefOpen(true); }}
                        >
                          <MapPin className="w-4 h-4 mr-1" /> Log Walk-in
                        </Button>
                        </>
                      )}
                      {!visits.some(v => v.status === "completed") && (
                      <Dialog open={showVisitDialog} onOpenChange={setShowVisitDialog}>
                        <DialogTrigger asChild>
                          <Button size="sm" data-testid="schedule-visit-btn">
                            <Plus className="w-4 h-4 mr-1" /> Schedule
                          </Button>
                        </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Schedule Site Visit</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <DynamicFormRenderer
                            formType="site_visit"
                            values={newVisit}
                            onChange={(field, value) => setNewVisit(prev => ({ ...prev, [field]: value }))}
                            projects={projects}
                            showGroups={false}
                            gridCols={1}
                          />
                          <Button onClick={handleScheduleVisit} className="w-full" data-testid="confirm-visit-btn">Schedule Visit</Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {visits.map((visit) => (
                        <div key={visit.visit_id} className={`p-4 rounded-lg border ${visit.status === 'completed' ? 'bg-green-50 border-green-200' : visit.status === 'cancelled' ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-500" />
                              <span className="font-medium">{new Date(visit.scheduled_date).toLocaleDateString()}</span>
                              {visit.visit_type === "revisit" && (
                                <span className="text-xs px-1.5 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 font-medium">Revisit</span>
                              )}
                            </div>
                            <span className={`text-xs px-2 py-1 rounded-full capitalize ${visit.status === 'completed' ? 'bg-green-100 text-green-700' : visit.status === 'scheduled' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
                              {visit.status}
                            </span>
                          </div>
                          {visit.project && <p className="text-sm text-slate-600">Project: {visit.project}</p>}
                          {visit.interest_level && <p className="text-sm text-slate-600">Interest: {visit.interest_level}</p>}
                          {visit.post_visit_feedback && (
                            <p className="text-sm text-slate-500 mt-2 bg-slate-50 p-2 rounded">{visit.post_visit_feedback}</p>
                          )}
                          {/* Action buttons for scheduled visits */}
                          {["scheduled", "confirmed"].includes(visit.status) && (
                            <div className="flex gap-2 mt-3">
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`complete-visit-${visit.visit_id}`}
                                onClick={() => {
                                  setDebriefVisitId(visit.visit_id);
                                  setDebriefOpen(true);
                                }}
                              >
                                <Check className="w-3.5 h-3.5 mr-1" /> Complete & Debrief
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                data-testid={`cancel-visit-${visit.visit_id}`}
                                onClick={async () => {
                                  try {
                                    await axios.put(`${API}/visits/${visit.visit_id}`, { status: "no_show" });
                                    toast.success("Visit marked as no-show");
                                    fetchLeadData();
                                  } catch { toast.error("Failed to update visit"); }
                                }}
                              >
                                No-show
                              </Button>
                            </div>
                          )}
                          {visit.status === "completed" && !visit.debrief_completed && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                                data-testid={`voice-debrief-${visit.visit_id}`}
                                onClick={() => {
                                  setVoiceLogDefaultType("site_visit_debrief");
                                  setVoiceLogSiteVisitId(visit.visit_id);
                                  setVoiceLogTaskId(null);
                                  setVoiceLogOpen(true);
                                }}
                              >
                                <Mic className="w-3.5 h-3.5 mr-1" /> Voice Debrief
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                      {visits.length === 0 && (
                        <p className="text-center text-slate-500 py-8">No site visits scheduled</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Routing History Tab */}
              <TabsContent value="routing">
                <Card className="bg-white border-slate-200 shadow-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg font-semibold font-['Manrope']">Routing History</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {(enrichment?.routing_history || []).map((log, i) => (
                        <div key={log.log_id || i} className="border-l-2 border-slate-200 pl-4 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-xs font-medium uppercase px-2 py-0.5 rounded-full ${
                              log.event_type === 'initial_assignment' ? 'bg-blue-100 text-blue-700' :
                              log.event_type === 'reassignment' ? 'bg-amber-100 text-amber-700' :
                              log.event_type === 'escalation' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {log.event_type?.replace(/_/g, ' ')}
                            </span>
                            <span className="text-xs text-slate-400">
                              {log.created_at ? new Date(log.created_at).toLocaleString() : "—"}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">{log.reason || "Routing decision"}</p>
                          {log.rule_name && <p className="text-xs text-slate-500 mt-1">Rule: {log.rule_name}</p>}
                          {log.assignment_method && <p className="text-xs text-slate-500">Method: {log.assignment_method}</p>}
                        </div>
                      ))}
                      {(!enrichment?.routing_history || enrichment.routing_history.length === 0) && (
                        <p className="text-center text-slate-500 py-8">No routing history</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="tools">
                <div className="space-y-6">
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <EMICalculator
                        apiBase={API}
                        leadId={lead.lead_id}
                        prefill={{
                          property_value: lead.budget_max || 7500000,
                        }}
                        showShareButton={true}
                        compact={true}
                      />
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold flex items-center gap-2">
                        MyHomeMatrix
                        <Badge variant="outline" className="text-xs">Priority Matching</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <LeadMhMSessionsPanel apiBase={API} leadId={lead.lead_id} leadName={lead.name} leadPhone={lead.phone} />
                      <div className="mt-4 pt-4 border-t border-dashed border-slate-200">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-400 mb-2">Start a new MhM session</p>
                        <MhMFlow
                          apiBase={API}
                          leadId={lead.lead_id}
                          showShareButton={true}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-white border-slate-200 shadow-sm">
                    <CardContent className="p-4">
                      <ProjectComparator
                        apiBase={API}
                        leadId={lead.lead_id}
                        showShareButton={true}
                      />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
            {lead.notes && (
              <Card className="bg-white border-slate-200 shadow-sm mt-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold font-['Manrope']">Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600">{lead.notes}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
      {/* Lost Reason Dialog */}
      <Dialog open={lostDialogOpen} onOpenChange={setLostDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="lost-reason-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-red-700 flex items-center gap-2">
              Mark Lead as Lost
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-sm font-medium mb-1.5 block">Reason *</Label>
              <Select value={lostReason} onValueChange={(v) => { setLostReason(v); setLostSubReason(""); }}>
                <SelectTrigger data-testid="lost-reason-select">
                  <SelectValue placeholder="Why is this lead lost?" />
                </SelectTrigger>
                <SelectContent>
                  {LOST_REASONS.map(r => (
                    <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {lostReason && LOST_REASONS.find(r => r.value === lostReason)?.subReasons?.length > 0 && (
              <div>
                <Label className="text-sm font-medium mb-1.5 block">Specific Reason</Label>
                <Select value={lostSubReason} onValueChange={setLostSubReason}>
                  <SelectTrigger data-testid="lost-sub-reason-select">
                    <SelectValue placeholder="Select specific reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOST_REASONS.find(r => r.value === lostReason).subReasons.map(sr => (
                      <SelectItem key={sr} value={sr}>{sr}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium mb-1.5 block">Additional Notes</Label>
              <Textarea
                placeholder="Any additional context..."
                value={lostNotes}
                onChange={(e) => setLostNotes(e.target.value)}
                rows={3}
                data-testid="lost-notes-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setLostDialogOpen(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={handleConfirmLost}
              disabled={!lostReason}
              data-testid="confirm-lost-btn"
            >
              Mark as Lost
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Backward Stage Confirmation Dialog */}
      <Dialog open={backwardStageDialog.open} onOpenChange={(open) => !open && setBackwardStageDialog({ open: false, targetStage: "" })}>
        <DialogContent className="sm:max-w-md" data-testid="backward-stage-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-amber-700">Moving Lead Backward</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                You're moving this lead from <strong className="capitalize">{lead?.stage?.replace(/_/g, " ")}</strong> back to <strong className="capitalize">{backwardStageDialog.targetStage.replace(/_/g, " ")}</strong>. This will be logged in the activity trail.
              </p>
            </div>
            <div>
              <Label>Reason for moving backward <span className="text-red-500">*</span></Label>
              <Textarea
                placeholder="e.g., Client went cold, needs re-engagement..."
                value={backwardReason}
                onChange={(e) => setBackwardReason(e.target.value)}
                rows={3}
                className="mt-1"
                data-testid="backward-reason-input"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBackwardStageDialog({ open: false, targetStage: "" })}>Cancel</Button>
              <Button
                onClick={handleConfirmBackwardStage}
                disabled={!backwardReason.trim()}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-backward-stage-btn"
              >
                Confirm Stage Change
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editLeadOpen} onOpenChange={setEditLeadOpen}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="edit-lead-dialog">
          <DialogHeader><DialogTitle>Edit Lead</DialogTitle></DialogHeader>
          {editLeadForm && (
            <div className="grid grid-cols-2 gap-3 py-2">
              <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Name</label><Input value={editLeadForm.name || ""} onChange={e => setEditLeadForm({...editLeadForm, name: e.target.value})} data-testid="edit-lead-name" /></div>
              <div><label className="text-sm font-medium mb-1 block">Phone</label><Input value={editLeadForm.phone || ""} onChange={e => setEditLeadForm({...editLeadForm, phone: e.target.value})} data-testid="edit-lead-phone" /></div>
              <div><label className="text-sm font-medium mb-1 block">Email</label><Input type="email" value={editLeadForm.email || ""} onChange={e => setEditLeadForm({...editLeadForm, email: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Source</label>
                <Select value={editLeadForm.source || ""} onValueChange={v => setEditLeadForm({...editLeadForm, source: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{SOURCES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium mb-1 block">Project</label>
                <Select value={editLeadForm.project_interest || ""} onValueChange={v => setEditLeadForm({...editLeadForm, project_interest: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{projects.map(p => <SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium mb-1 block">Configuration</label>
                <Select value={editLeadForm.configuration || ""} onValueChange={v => setEditLeadForm({...editLeadForm, configuration: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CONFIGURATIONS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium mb-1 block">Budget Min</label><Input type="number" value={editLeadForm.budget_min || 0} onChange={e => setEditLeadForm({...editLeadForm, budget_min: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Budget Max</label><Input type="number" value={editLeadForm.budget_max || 0} onChange={e => setEditLeadForm({...editLeadForm, budget_max: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Locality</label><Input value={editLeadForm.locality_preference || ""} onChange={e => setEditLeadForm({...editLeadForm, locality_preference: e.target.value})} /></div>
              <div><label className="text-sm font-medium mb-1 block">Next Action</label><Input value={editLeadForm.next_action || ""} onChange={e => setEditLeadForm({...editLeadForm, next_action: e.target.value})} /></div>
              <div className="col-span-2"><label className="text-sm font-medium mb-1 block">Notes</label><Textarea value={editLeadForm.notes || ""} onChange={e => setEditLeadForm({...editLeadForm, notes: e.target.value})} rows={3} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditLeadOpen(false)}>Cancel</Button>
            <Button onClick={saveEditLead} data-testid="save-edit-lead-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer to Project Dialog */}
      <Dialog open={showTransferDialog} onOpenChange={setShowTransferDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-600" />
              Transfer Lead to Another Project
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>Current:</strong> {lead?.name} → {lead?.project_interest || "No project assigned"}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                This will create a linked lead for the new project. The original lead will be preserved.
              </p>
            </div>
            
            <div>
              <Label>Transfer to Project *</Label>
              <Select value={transferProject} onValueChange={setTransferProject}>
                <SelectTrigger data-testid="transfer-project-select">
                  <SelectValue placeholder="Select new project" />
                </SelectTrigger>
                <SelectContent>
                  {projects
                    .filter(p => p.name.toLowerCase() !== (lead?.project_interest || "").toLowerCase())
                    .map(p => (
                      <SelectItem key={p.project_id} value={p.name}>{p.name}</SelectItem>
                    ))
                  }
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Transfer Notes (optional)</Label>
              <Textarea
                placeholder="e.g., Lead showed interest in Green Valley during site visit discussion..."
                value={transferNotes}
                onChange={(e) => setTransferNotes(e.target.value)}
                rows={3}
                data-testid="transfer-notes-input"
              />
            </div>

            <div className="flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => { setShowTransferDialog(false); setTransferProject(""); setTransferNotes(""); }}
              >
                Cancel
              </Button>
              <Button 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
                onClick={handleTransferToProject}
                disabled={!transferProject || transferring}
                data-testid="confirm-transfer-btn"
              >
                {transferring ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Transferring...</>
                ) : (
                  <><ArrowRightLeft className="w-4 h-4 mr-2" /> Transfer Lead</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Re-engage Lost Lead Dialog */}
      <Dialog open={reEngageDialog.open} onOpenChange={(open) => !open && setReEngageDialog({ open: false, notes: "" })}>
        <DialogContent className="sm:max-w-md" data-testid="re-engage-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Zap className="w-5 h-5 text-rose-500" />
              Re-engage this lead?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
              <p className="text-sm text-rose-800">
                This lead is currently <strong>Lost</strong>. Since you just logged a call, would you like to move them back into the active pipeline as <strong>Re-engaged</strong>?
              </p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                placeholder="e.g., Customer called back showing interest in 3BHK..."
                value={reEngageDialog.notes}
                onChange={(e) => setReEngageDialog({ ...reEngageDialog, notes: e.target.value })}
                rows={3}
                data-testid="re-engage-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setReEngageDialog({ open: false, notes: "" })} data-testid="re-engage-skip-btn">
              No, keep as Lost
            </Button>
            <Button onClick={handleReEngage} className="bg-rose-600 hover:bg-rose-700" data-testid="re-engage-confirm-btn">
              <Zap className="w-4 h-4 mr-1" /> Re-engage Lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repeat Buyer Enquiry Dialog */}
      <Dialog open={repeatBuyerDialog} onOpenChange={setRepeatBuyerDialog}>
        <DialogContent className="sm:max-w-md" data-testid="repeat-buyer-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <span className="text-xl">🏠</span>
              Add Another Enquiry
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>{lead?.name}</strong> has already booked in <strong>{lead?.project_interest || "this project"}</strong>. 
                Create a new enquiry for another unit?
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Phone:</span>
                <p className="font-medium">{lead?.phone}</p>
              </div>
              <div>
                <span className="text-slate-500">Project:</span>
                <p className="font-medium">{lead?.project_interest || "N/A"}</p>
              </div>
              <div>
                <span className="text-slate-500">Budget:</span>
                <p className="font-medium">{formatINR(lead?.budget_min)} - {formatINR(lead?.budget_max)}</p>
              </div>
              <div>
                <span className="text-slate-500">Config:</span>
                <p className="font-medium">{lead?.configuration || "N/A"}</p>
              </div>
            </div>
            <div>
              <Label>Notes for new enquiry</Label>
              <Textarea
                placeholder="e.g., Interested in 3BHK for son's family, looking at higher floors..."
                value={repeatBuyerNotes}
                onChange={(e) => setRepeatBuyerNotes(e.target.value)}
                rows={3}
                data-testid="repeat-buyer-notes"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setRepeatBuyerDialog(false); setRepeatBuyerNotes(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRepeatBuyerEnquiry} 
              disabled={creatingRepeatBuyer}
              className="bg-amber-600 hover:bg-amber-700" 
              data-testid="create-repeat-buyer-btn"
            >
              {creatingRepeatBuyer ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
              ) : (
                <><Plus className="w-4 h-4 mr-2" /> Create Enquiry</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Lead Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="delete-lead-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" /> Delete Lead
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Are you sure you want to delete this lead? This action cannot be undone.
            </p>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="font-medium text-slate-900">{lead?.name}</p>
              <p className="text-sm text-slate-500">{lead?.phone} • {lead?.email || "No email"}</p>
              <p className="text-xs text-slate-400 mt-1">Lead ID: {lead?.lead_id}</p>
            </div>
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> All associated data will be deleted:
              </p>
              <ul className="text-xs text-red-600 mt-1 list-disc list-inside">
                <li>Interactions & call logs</li>
                <li>Tasks & site visits</li>
                <li>Notes & documents</li>
                <li>EOI & deal room (if any)</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteLead} 
              disabled={deleting}
              data-testid="confirm-delete-lead-btn"
            >
              {deleting ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Deleting...</>
              ) : (
                <><Trash2 className="w-4 h-4 mr-2" /> Delete Lead</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tag to Campaign Dialog */}
      <Dialog open={campaignDialogOpen} onOpenChange={setCampaignDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="tag-campaign-dialog">
          <DialogHeader>
            <DialogTitle>Tag Lead to Campaign</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Select a campaign to tag this lead to. This helps track campaign performance.
            </p>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger data-testid="campaign-select">
                <SelectValue placeholder="Select a campaign..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">No campaign</SelectItem>
                {campaigns.map(c => (
                  <SelectItem key={c.campaign_id} value={c.campaign_id}>
                    {c.name} {c.source && `(${c.source})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {lead?.latest_campaign && (
              <p className="text-xs text-slate-500">
                Currently tagged to: <span className="text-purple-600 font-medium">{lead.latest_campaign}</span>
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setCampaignDialogOpen(false); setSelectedCampaign(""); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleTagCampaign} 
              disabled={taggingCampaign || !selectedCampaign}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-tag-campaign-btn"
            >
              {taggingCampaign ? (
                <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Tagging...</>
              ) : (
                "Tag to Campaign"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pre-Call Copilot Dialog */}
      <PreCallCopilot
        leadId={leadId}
        leadName={lead?.name || "Lead"}
        open={prepMeOpen}
        onClose={() => setPrepMeOpen(false)}
      />

      {/* Meeting Debrief Dialog */}
      <MeetingDebrief
        leadId={leadId}
        leadName={lead?.name || "Lead"}
        leadProject={lead?.project_interest || ""}
        open={debriefOpen}
        onClose={() => { setDebriefOpen(false); setDebriefVisitId(null); }}
        visitIdToComplete={debriefVisitId}
        onComplete={() => { fetchLeadData(); setDebriefVisitId(null); }}
      />

      {/* Visit Prep Dialog */}
      <VisitPrepDialog
        leadId={leadId}
        leadName={lead?.name || "Lead"}
        open={visitPrepOpen}
        onClose={() => setVisitPrepOpen(false)}
      />

      {/* Voice Log Dialog */}
      <VoiceLogDialog
        open={voiceLogOpen}
        onOpenChange={setVoiceLogOpen}
        leadId={leadId}
        defaultType={voiceLogDefaultType}
        taskId={voiceLogTaskId}
        siteVisitId={voiceLogSiteVisitId}
        onSaved={() => fetchLeadData()}
      />

      {/* Live Site Visit Dialog (multi-clip field journaling) */}
      <LiveSiteVisitDialog
        open={liveVisitOpen}
        onOpenChange={setLiveVisitOpen}
        leadId={leadId}
        onSaved={() => fetchLeadData()}
      />

      {/* Negotiation Form Dialog */}
      <NegotiationForm
        leadId={leadId}
        lead={lead}
        open={negotiationFormOpen}
        onClose={() => setNegotiationFormOpen(false)}
        onComplete={() => { fetchLeadData(); }}
      />
    </Layout>
  );
};

export default LeadDetailPage;
